import { z } from 'zod';
import { activitySchema, biasSchema, coordSchema, exposedConstraintSchema, meetingStatusSchema, participantRoleSchema, rejectReasonSchema, } from './domain.js';
/**
 * Wire contracts - meet-spec.md §6.
 *
 * `GET /:token` is **role-scoped**: the creator's payload never contains the
 * recipient's origin, and vice versa. That is expressed here as two distinct
 * view types, not one type with optional fields - an optional field is a
 * leak waiting for a careless serializer.
 */
// ─── POST /meetings ─────────────────────────────────────────────────────────
export const createMeetingBodySchema = z.object({
    activity: activitySchema,
    meetAt: z.string().datetime({ offset: true }),
    bias: biasSchema.optional().default('fair'),
    constraints: z.array(exposedConstraintSchema).max(2).optional().default([]),
    /**
     * Alice's origin, captured at creation. Coarsened server-side immediately.
     *
     * Optional, because a pinned area makes it moot: her origin exists to place
     * the corridor, and naming an area answers that question outright. Asking
     * for a location that changes nothing is asking for a location for no
     * reason.
     */
    origin: coordSchema.optional(),
    originLabel: z.string().max(80).optional(),
    /**
     * Pin the meeting to a postcode area instead of the corridor between the two
     * origins. Not a filter — a different question: 'somewhere in the 7th' and
     * 'somewhere fair between us' are both legitimate asks.
     *
     * An id, not a position: "@48.2019,16.3487" for a dropped pin. The server
     * re-resolves it, so what gets stored is always something it derived itself
     * rather than whatever a request claimed.
     */
    areaId: z.string().max(48).optional(),
});
export const createMeetingResponseSchema = z.object({
    token: z.string(),
    shareUrl: z.string().url(),
});
// ─── POST /meetings/:token/origin ───────────────────────────────────────────
export const submitOriginBodySchema = z.object({
    origin: coordSchema,
    originLabel: z.string().max(80).optional(),
});
// ─── POST /meetings/:token/opt-out ──────────────────────────────────────────
/** The reason is optional - someone who will not explain themselves still leaves. */
export const rejectBodySchema = z.object({
    reason: rejectReasonSchema.optional(),
});
// ─── POST /meetings/:token/pick ─────────────────────────────────────────────
/** A venue chosen by place id. Ends in CONFIRMED. */
export const pickBodySchema = z.object({
    placeId: z.string().min(1),
});
// ─── POST /meetings/:token/push ─────────────────────────────────────────────
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().url(),
    keys: z.object({ p256dh: z.string(), auth: z.string() }),
});
// ─── GET /meetings/:token ───────────────────────────────────────────────────
/** A venue as shown on a card. Both roles see the same venue facts. */
export const venueViewSchema = z.object({
    placeId: z.string(),
    name: z.string(),
    /** Present so the card can render a map pin. Not the participant's location. */
    location: coordSchema,
    rating: z.number().nullable(),
    /**
     * Shown next to the rating. A bare "4.5" is the thing that sends people to
     * Google Maps to check whether it means anything - "4.5 (8795)" does not.
     */
    reviewCount: z.number().int().min(0),
    priceLevel: z.number().int().min(1).max(4).nullable(),
    /** Google `primaryType`, for the card's activity label ("Café", "Bar"). */
    primaryType: z.string().nullable(),
    /**
     * Whether a thumbnail exists at /meetings/:token/photo. A boolean, not a
     * URL: the photo is proxied so no Google URL or key reaches the browser.
     */
    hasPhoto: z.boolean(),
    /**
     * Venue phone, for a one-tap reservation call. Null when Google has none.
     * Shown only once the meeting is confirmed - calling to book a place that
     * is still being chosen makes no sense.
     */
    phone: z.string().nullable(),
    /**
     * True when a pinned area was requested and this place sits outside it.
     * An area with three cafés open on a Tuesday must still produce
     * suggestions (invariant 9), so the search widens rather than returning
     * nothing - and the card says so instead of quietly pretending.
     */
    outsideArea: z.boolean(),
    /**
     * Null when the meeting is pinned to an area: the creator never gave an
     * origin, so there is no second travel time and no fairness claim to make.
     * The card falls back to the recipient's own time, which is the only honest
     * thing it knows.
     */
    etaCreatorS: z.number().int().nullable(),
    /**
     * Null for a pinned-area meeting, where neither gave an origin: the area
     * was chosen outright, so nothing was routed and there is no travel time to
     * state. The card shows what it knows - name, rating, price - rather than a
     * number it would have to invent.
     */
    etaRecipientS: z.number().int().nullable(),
});
const meetingBaseSchema = z.object({
    status: meetingStatusSchema,
    activity: activitySchema,
    meetAt: z.string().datetime({ offset: true }),
    role: participantRoleSchema,
    /**
     * Chips for the exit question - 'why weren't these what you expected?',
     * asked once as the recipient leaves. They used to sit under the cards and
     * spend a rejection; they now only record why.
     */
    rejectChips: z.array(rejectReasonSchema),
    /**
     * The area Alice pinned, if any. The recipient is told which area the
     * suggestions come from - otherwise a set clustered in one postcode looks
     * arbitrary rather than chosen.
     */
    areaName: z.string().nullable(),
});
/**
 * Recipient view. Carries the current suggestion - this is Bob's screen.
 * Never carries the creator's origin.
 */
export const recipientViewSchema = meetingBaseSchema.extend({
    role: z.literal('recipient'),
    /**
     * The current pick - first of `venues`, and the one `/accept` confirms.
     * Kept as its own field so accept and the photo proxy both keep meaning
     * "the one at the top" without every caller indexing an array.
     */
    venue: venueViewSchema.nullable(),
    /**
     * The top few options, best first.
     *
     * A departure from invariant 5, made deliberately: the spec's bet is that
     * comparison is the disease, so watch seconds-to-confirmed (§15) - if it
     * rises after this change, this is why. Three is the cap, so it stays a
     * short list rather than a feed.
     */
    venues: z.array(venueViewSchema),
});
/**
 * Creator view. Alice sees a venue only once CONFIRMED - invariant 3: she
 * cannot reject what she never saw.
 */
export const creatorViewSchema = meetingBaseSchema.extend({
    role: z.literal('creator'),
    /** Non-null only in CONFIRMED / REMINDED. */
    venue: venueViewSchema.nullable(),
    shareUrl: z.string().url(),
    /** Whether the recipient has opened the link at all. */
    opened: z.boolean(),
});
export const meetingViewSchema = z.discriminatedUnion('role', [
    recipientViewSchema,
    creatorViewSchema,
]);
// ─── Errors ─────────────────────────────────────────────────────────────────
export const apiErrorSchema = z.object({
    error: z.string(),
    message: z.string().optional(),
});
//# sourceMappingURL=api.js.map